import { LEGAL_LINKS_SLOT } from 'constants/footer';
import { trackOperation } from 'events/ops/opsEvent';
import { logErrorAndLogToServer } from 'middleware/logger';
import type { MissingFooterSlots } from 'types/headerFooter';

/**
 * Emits missing-data ops metrics for footer slots from the RAW ZCS footer outcome captured by the
 * HF cache (RemoteCache) and threaded to the client via Redux.
 *
 * Legal-links is tracked on its own operation (legal/privacy compliance surface); all other required
 * footer slots share a single operation. On a total footer outage both operations fail.
 *
 * A no-op when the outcome is undefined (cold cache, or a marketplace with no required-slots list).
 */
export function trackFooterSlotDataOps(result: MissingFooterSlots | undefined): void {
  if (!result) {
    return;
  }

  // No footer content at all — every slot (including legal-links) is missing; fail both operations.
  if (result.footerContentMissing) {
    logErrorAndLogToServer(
      JSON.stringify({
        operation: 'FooterSlotDataLoad',
        reason: 'missing-footer-content',
        message: 'ZCS returned no footer content — all required slots missing',
        missingSlots: 'all-slots'
      })
    );
    trackOperation('FooterLegalLinksSlotDataLoad', 'failure');
    trackOperation('FooterSlotDataLoad', 'failure');
    return;
  }

  // Split the missing slots: legal-links on its own operation; all other required slots shared.
  const legalLinksMissing = result.missingSlots.includes(LEGAL_LINKS_SLOT);
  const nonLegalMissingSlots = result.missingSlots.filter(slot => slot !== LEGAL_LINKS_SLOT);

  if (legalLinksMissing) {
    logErrorAndLogToServer(
      JSON.stringify({
        operation: 'FooterLegalLinksSlotDataLoad',
        reason: 'missing-legal-links-slot',
        message: 'ZCS footer response missing the "legal-links" slot',
        missingSlots: [LEGAL_LINKS_SLOT]
      })
    );
    trackOperation('FooterLegalLinksSlotDataLoad', 'failure');
  } else {
    trackOperation('FooterLegalLinksSlotDataLoad', 'success');
  }

  if (nonLegalMissingSlots.length > 0) {
    logErrorAndLogToServer(
      JSON.stringify({
        operation: 'FooterSlotDataLoad',
        reason: 'missing-footer-slots',
        message: 'ZCS footer response missing required slot(s)',
        missingSlots: nonLegalMissingSlots
      })
    );
    trackOperation('FooterSlotDataLoad', 'failure');
  } else {
    trackOperation('FooterSlotDataLoad', 'success');
  }
}
